/**
 * Dictionnaire bilingue de la home (`/` FR · `/en` EN).
 *
 * Centralise TOUT le contenu + le "chrome" de la landing page (jadis codé en
 * dur dans page.tsx + les composants de section). La vue partagée LandingView
 * et les sections lisent `LANDING[locale]` → un seul markup pour FR et EN,
 * zéro composant dupliqué, zéro texte FR côté EN.
 *
 * Les visuels (StaticImageData) restent dans la vue/les composants (identiques
 * FR/EN) ; ici uniquement les chaînes + la structure de contenu. Les libellés
 * du formulaire de réservation vivent dans reservationFormUi.ts (réutilisé).
 *
 * `href(locale, frPath)` : mappe un chemin FR → son équivalent EN quand une
 * version EN existe (sinon garde le FR). Les liens FR restent intacts.
 */
import type { Locale } from "@/config/i18n";
import { type I18n } from "@/config/localeText";
import { localeNavHref } from "@/config/localeRoutes";
import { LANDING_FR } from "./landingDict.fr";
import { LANDING_EN } from "./landingDict.en";
import { LANDING_AR } from "./landingDict.ar";

export type LandingData = {
  /** h1 sr-only de la page. */
  pageTitle: string;
  hero: {
    driverTitle: string;
    driverDescription: string;
    driverCta: string;
    rentalTitle: string;
    rentalDescription: string;
    rentalCta: string;
    driverImageAlt: string;
    rentalImageAlt: string;
    scrollHint: string;
  };
  clients: {
    heading: string;
    divider: string;
    reviewsHeading: string;
    reviews: { quote: string; author: string }[];
  };
  about: {
    heading: string;
    intro: string;
    cta: string;
    items: { icon: string; title: string; desc: string }[];
  };
  services: {
    eyebrow: string;
    heading: string;
    intro: string;
    driverTitle: string;
    driverBadge: string;
    driverLead: string;
    driverSub: string;
    driverItems: string[];
    /** Intro de la ligne de villes (liens vers les pages chauffeur locales). */
    driverCitiesLabel: string;
    /** Libellés, dans l'ordre de DRIVER_CITY_HREFS (ServicesSection). */
    driverCities: string[];
    driverCtaPrimary: string;
    driverCtaSecondary: string;
    rentalTitle: string;
    rentalBadge: string;
    rentalLead: string;
    rentalSub: string;
    rentalItems: string[];
    rentalCtaPrimary: string;
    rentalCtaSecondary: string;
    note: string;
  };
  gallery: {
    eyebrow: string;
    heading: string;
    intro: string;
    bullets: string[];
    ctaPrimary: string;
    ctaSecondary: string;
    prevLabel: string;
    nextLabel: string;
    pauseLabel: string;
    resumeLabel: string;
    dotLabel: (n: number) => string;
    carFallbackAlt: string;
  };
  excursions: {
    heading: string;
    discover: string;
    /** Titres EN des cartes excursions (FR lit excursionsCards directement). */
    cardTitles?: string[];
  };
  enterprise: {
    eyebrow: string;
    heading: string;
    text: string;
    ctaContact: string;
    ctaReserve: string;
  };
  valueProps: {
    eyebrow: string;
    heading: string;
    intro: string;
    reviewsEyebrow: string;
    googleEyebrow: string;
    googleNote: string;
    catalogueLabel: string;
    modelsAvailable: (n: number) => string;
    reviews: { quote: string; source: string; tag: string }[];
    cards: { title: string; description: string; label: string }[];
    fleetAlt: string;
    eventsAlt: string;
  };
  universe: {
    heading: string;
    intro: string;
    brandFilmAlt: string;
    socialProofAlt: string;
    fleetUniverseAlt: string;
  };
  orient: {
    heading: string;
    links: { label: string; frPath: string }[];
  };
};

export const LANDING: I18n<LandingData> = {
  fr: LANDING_FR,
  en: LANDING_EN,
  ar: LANDING_AR,
};

/**
 * Mappe un chemin FR vers son équivalent EN quand une version EN existe.
 * En FR (ou chemin non mappé) renvoie le chemin tel quel → liens FR intacts.
 * Préserve les ancres (#catalogue-location) et le hash éventuel.
 */
const FR_TO_EN_PATH: Record<string, string> = {
  "/chauffeur": "/en/chauffeur",
  "/excursions": "/en/excursions",
  "/location/vehicules": "/en/location/vehicules",
  "/location": "/en/location",
  "/reservation": "/en/reservation",
  "/contact": "/en/contact",
  "/location/offres": "/en/location/rental-offers",
  "/chauffeur/services": "/en/chauffeur/services",
  "/a-propos": "/en/about",
};

export const href = (locale: Locale, frPath: string): string => {
  if (locale === "fr") return frPath;
  // L'arabe passait par la table EN : l'accueil /ar envoyait vers /en/...
  if (locale === "ar") return localeNavHref(frPath, locale);
  const [path, hash] = frPath.split("#");
  const mapped = FR_TO_EN_PATH[path];
  if (!mapped) return frPath;
  return hash ? `${mapped}#${hash}` : mapped;
};
